/**
 * `/checkout/mock/$ref` — search schema.
 *
 * The mock processor has no hosted page of its own (PAY-1): `lib/payments/
 * provider-mock.ts` mints `redirectUrl: /checkout/mock/${checkoutRef}` for every
 * `createCheckout` and `retrieveCheckout` call, on EVERY subject kind it serves — a store
 * order, a subscription, a talent engagement — because "there is no hosted page to send a
 * buyer to, and a fabricated vendor URL would be a dead link in a receipt" (same file,
 * same comment). This route is that page: the honest local one the comment promised and
 * that nothing before PAY-1 ever built, which is why the browser used to land on the
 * router's catch-all "Not Found" after a real Subscribe or Buy click succeeded server-side.
 *
 * DELIBERATELY NOT UNDER `/market` (PAY-1 review round 2). A subscription checkout is
 * gated on `VITE_BILLING` alone — `POST /api/billing/subscription/checkout` needs no
 * marketplace stage at all — but this page first shipped at
 * `/market/checkout/mock/$ref`, a filesystem child of `/market/checkout`, which made it
 * inherit `market.tsx`'s `beforeLoad` redirect (kicks to `/` unless `VITE_MARKETPLACE` or
 * `VITE_SKILLS_STORE` is baked in) and `market.lazy.tsx`'s own capability gate
 * (`<DisabledPanel/>` when the gateway reports marketplace disabled,
 * `<CapabilityUnavailable area="Market"/>` when no stage is open for the account) — both
 * of which replace the `<Outlet/>` this route needs to render through. A billing-on/
 * market-off account, or one with no marketplace stage open, subscribed successfully on
 * the server and then landed on one of those panels instead of this page. Living directly
 * under `/checkout` (a sibling of `/market`, both children of the pathless
 * `_authenticated` layout) answers to no feature gate but being signed in — which is all
 * a mock checkout return page should ever require — and it also means `checkout.lazy.tsx`
 * (the `/market/checkout` order-creation screen) no longer has a filesystem child to hand
 * an `<Outlet/>` to: that coupling is gone, not just untested.
 *
 * The ref is not validated here. It carries no meaning to the browser beyond being the
 * thing to show back to the person (`mock_<kind>_<sha1>` — see `provider-mock.ts`'s `ref()`)
 * — this screen does not look anything up BY it. See the `.lazy.tsx` file for why: the ref
 * is a webhook lookup key, and turning it into a second, browser-reachable lookup key for
 * "whose order is this" is a new attack surface this fix does not need to open. The subject
 * of the checkout tells its OWN result through the existing, account-scoped read (Plan &
 * Billing's `GET /api/billing/subscription`) plus a set of honest links to the places a
 * buyer already checks — the receipt list, the library, Plan & Billing — the same three
 * places a REAL Stripe return would send three DIFFERENT checkouts to (`checkout-return`
 * for a pack order, `/market/engagements?checkout=return` for talent, `/settings/usage`
 * for a subscription — see `store-checkout.ts`'s and `billing-subscription-routes.ts`'s
 * `returnUrl`s). This one page serves all three because the mock's redirect does not say
 * which of them minted the ref.
 */
import { createFileRoute } from '@tanstack/react-router';

export const Route = createFileRoute('/_authenticated/checkout/mock/$ref')({});
